import React, { useRef, useMemo, useEffect } from "react";
import { useInput, useTranslate } from "react-admin";
import { TextField, InputAdornment, Box, Tooltip } from "@mui/material";

const sanitizeHex6 = (v) =>
  (v || "").toString().replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();

// 將任意值轉成 TextField 可接受的 ReactNode
const toHelperNode = (v) => {
  if (v == null || typeof v === "boolean") return undefined;
  if (typeof v === "string" || typeof v === "number") return v;
  return React.isValidElement(v) ? v : undefined;
};

export default function HexColorPicker({
  source = "code",
  label = "HEX色碼",
  helperText,
  defaultHex,         // 可選：沒有值時顯示的預設色（不含#）
  autofillOnMount,    // 可選：是否在掛載時把 defaultHex 寫入表單
  disabled,
  ...props
}) {
  const translate = useTranslate();

  const {
    field,               // { name, value, onChange, onBlur, ref }
    fieldState: { error },
    isRequired,          // 由 validate={[required()]} 自動帶入
  } = useInput({ source, disabled, ...props });

  const pickerRef = useRef(null);

  // 需要時把 defaultHex 寫入表單（建立頁想預填時可用）
  useEffect(() => {
    if (!autofillOnMount || !defaultHex) return;
    if (!field.value) {
      const hex = sanitizeHex6(defaultHex);
      if (hex) field.onChange(hex);
    }
  }, [autofillOnMount, defaultHex, field]);

  // 顯示值：文字一定帶 #，色塊一定是 #RRGGBB
  const fallbackHex = defaultHex ? sanitizeHex6(defaultHex) : "";
  const currentHex = useMemo(
    () => sanitizeHex6(field.value || fallbackHex),
    [field.value, fallbackHex]
  );

  const displayText  = currentHex ? `#${currentHex}` : "";
  const displayColor = currentHex ? `#${currentHex}` : "#000000";

  const handleTextChange = (e) => {
    const hex = sanitizeHex6(e.target.value.replace(/^#/, ""));
    field.onChange(hex || null); // 表單內存不含 #
  };

  const handlePick = (e) => {
    const hex = sanitizeHex6(e.target.value.replace(/^#/, ""));
    field.onChange(hex || null);
  };

  const rawMsg =
    typeof error === "string"
      ? error
      : error?.message?.message; // e.g. 'ra.validation.required' or 自訂字串

  const translated =
    rawMsg != null
      ? translate(rawMsg, {
          // 若 i18n 沒對應，就用原字串
          _: rawMsg,
          ...(error?.args || error?.messageArgs || {}),
        })
      : undefined;

  const helperNode =
    toHelperNode(translated) ??
    toHelperNode(helperText) ??
    " "; // 占位避免高度跳動

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
      {/* 隱藏 color input：用來喚起原生選色器 */}
      <input
        type="color"
        ref={pickerRef}
        value={displayColor}
        onChange={handlePick}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        tabIndex={-1}
        aria-hidden
      />

      <TextField
        label={label}
        required={isRequired}     // ★ 會顯示紅色星號（和 TextInput 一樣）
        name={field.name}
        inputRef={field.ref}
        value={displayText}       // 顯示 #RRGGBB
        onChange={handleTextChange}
        onBlur={field.onBlur}
        error={Boolean(error)}    // ★ 有錯就紅色描邊（和 TextInput 一樣）
        helperText={helperNode}   // ★ 顯示翻譯後的錯誤或自訂 helperText
        disabled={disabled}
        inputProps={{ maxLength: 7 }}
        sx={{ width: '50%' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <Tooltip title="點擊選色">
                <Box
                  onClick={() => !disabled && pickerRef.current?.click()}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0.75,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    bgcolor: displayColor,
                    cursor: disabled ? "not-allowed" : "pointer",
                  }}
                />
              </Tooltip>
            </InputAdornment>
          ),
          onWheel: (e) => e.target.blur(),
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 56 /* align with TextField */ }}>
        <span>快速選擇：</span>
        {[
          { name: "黑", hex: "000000" },
          { name: "白", hex: "FFFFFF" },
          { name: "灰", hex: "808080" },
        ].map(({ name, hex }) => (
          <Tooltip title={`設定為 ${name} #${hex}`} key={hex}>
            <Box
              onClick={() => !disabled && field.onChange(hex)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: disabled ? "not-allowed" : "pointer",
                gap: 0.5
              }}
            >
              {name}
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 0.75,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: `#${hex}`,
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}
